// pages/ItemDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { Edit, X, Save, ArrowLeft,  } from "lucide-react";
import JoditEditor from 'jodit-react';

const API_BASE_URL = "https://backend.dlapakistan.com/api/ourpolicies";

const ItemDetail = () => {
  const { policySlug, itemSlug } = useParams();
  const navigate = useNavigate();

  const [item, setItem] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [heading, setHeading] = useState("");
  const [content, setContent] = useState("");
  
  // Success notification state - PolicyDetail के समान
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const config = {
    readonly: false,
    placeholder: 'Enter section content here...',
    buttons: ['bold', 'italic', 'underline', 'link', 'ul', 'ol', 'font', 'fontsize', 'brush', 'paragraph'],
    height: 400,
    theme: 'default',
    style: {
      background: '#f9fafb',
    },
  };

  useEffect(() => {
    fetchData();
  }, [policySlug, itemSlug]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const policyRes = await axios.get(`${API_BASE_URL}/${policySlug}/`);
      const itemRes = await axios.get(
        `${API_BASE_URL}/${policySlug}/items/${itemSlug}/`
      );
      setPolicy(policyRes.data);
      setItem(itemRes.data);
      setHeading(itemRes.data.heading || "");
      setContent(itemRes.data.content || "");
    } catch {
      Swal.fire("Error", "Section not found", "error");
      navigate(`/ourpolicies/${policySlug}`);
    } finally {
      setLoading(false);
    }
  };

  // PolicyDetail
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessNotification(true);
    setTimeout(() => {
      setShowSuccessNotification(false);
    }, 3000);
  };

  const saveItem = async () => {
    if (!heading.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Heading is required',
        text: 'Please provide a heading for the section',
        confirmButtonColor: '#059669',
        confirmButtonText: 'OK',
        timer: 2000
      });
      return;
    }
    
    try {
      setSaving(true);
      const res = await axios.put(
        `${API_BASE_URL}/${policySlug}/items/${itemSlug}/`,
        {
          title: item.title,
          heading,
          content: content,
          is_active: item.is_active,
          order: item.order
        }
      );
      setItem(res.data);
      setShowEditModal(false);
      
      // PolicyDetail
      showSuccess("Section updated successfully");
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Failed to update section',
        text: 'Please try again',
        confirmButtonColor: '#059669',
        confirmButtonText: 'OK'
      });
    } finally {
      setSaving(false);
    }
  };
  

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading section details...</p>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Success Notification*/}
      {showSuccessNotification && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 sm:left-auto sm:transform-none sm:right-4 z-50 animate-fadeIn w-[90%] sm:w-auto max-w-sm">
          <div className="bg-white border border-emerald-200 rounded-2xl shadow-xl p-4 sm:p-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="font-bold text-gray-800 text-sm sm:text-base truncate">Success</p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">{successMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Bar*/}
      <div className="w-full sticky top-0 z-40 pt-4 sm:pt-0 sm:top-16">
        <div className="w-full px-4 sm:px-6 md:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
            <div className="text-left">
              <div className="flex items-center gap-3 sm:gap-4">
                <button
                  onClick={() => navigate(`/About-Dla/OurPolicies/${policySlug}`)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
                  aria-label="Go back"
                >
                  <ArrowLeft size={20} className="text-gray-600" />
                </button>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 break-words">Section Management</h1>
              </div>
            </div>

            <div className="flex items-center gap-2 self-start">
              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center justify-center px-4 sm:px-6 py-2.5 bg-emerald-600 text-white rounded-lg font-medium shadow-md hover:opacity-90 transition-all text-sm sm:text-base whitespace-nowrap"
              >
                <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                Edit Section
              </button>
         
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - PolicyDetail*/}
      <div className="w-full mt-4 sm:mt-8 md:mt-20 py-4 sm:py-8">
        <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12 pb-8 sm:pb-12 md:pb-20">
          {/* Main Section Card */}
          <div className="bg-transparent w-full">
            {/* Header Section */}
            <div className="mb-6 sm:mb-8 md:mb-10 border-l-4 pl-4 sm:pl-6" style={{ borderColor: '#059669' }}>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 tracking-tight break-words">
                {item?.heading || item?.title || "Section Details"}
              </h2>
       
            </div>
      
           
            {/* Content Body - PolicyDetail */}
            <div className="mt-6 sm:mt-8 md:mt-12 border-t border-gray-200 pt-6 sm:pt-8 md:pt-10 w-full bg-transparent">
              <div 
                className="custom-description w-full prose prose-emerald max-w-none bg-transparent" 
                dangerouslySetInnerHTML={{ 
                  __html: item?.content || '<div class="flex flex-col items-center justify-center py-8 sm:py-12 md:py-16 text-center px-4"><div class="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3 sm:mb-4"><FileText className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" /></div><h3 class="text-lg sm:text-xl font-medium text-gray-500 mb-2 break-words">No section content available</h3><p class="text-gray-400 text-sm sm:text-base">Click on "Edit Section" to add content</p></div>' 
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Edit Section Modal - PolicyDetail*/}
      {showEditModal && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={() => !saving && setShowEditModal(false)} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-2 sm:p-4 pointer-events-none">
            <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl shadow-2xl w-full max-w-full sm:max-w-2xl md:max-w-3xl mx-2 sm:mx-4 pointer-events-auto overflow-hidden animate-modalIn border border-gray-100 max-h-[90vh] flex flex-col">
              {/* Modal Header */}
              <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 border-b flex justify-between items-center bg-gray-50/50 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-bold text-gray-800 truncate">
                  Edit Section
                </h2>
                <button 
                  onClick={() => !saving && setShowEditModal(false)} 
                  disabled={saving}
                  className="p-1 sm:p-2 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                  aria-label="Close"
                >
                  <X size={18} className="sm:w-5 sm:h-5" />
                </button>
              </div>

              {/* Modal Content */}
              <form onSubmit={(e) => { e.preventDefault(); saveItem(); }} className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6 overflow-y-auto flex-1">
                {/* Title Display */}
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2 uppercase tracking-wider">Section Title</label>
                  <div className="w-full px-3 sm:px-4 md:px-5 py-2 sm:py-3 bg-gray-50 border border-gray-200 rounded-lg sm:rounded-xl md:rounded-2xl text-gray-700 text-sm sm:text-base break-words">
                    {item?.title || "Untitled Section"}
                  </div>
                </div>

                {/* Heading Input */}
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2 uppercase tracking-wider">
                    Section Heading <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={heading}
                    onChange={(e) => setHeading(e.target.value)}
                    className="w-full px-3 sm:px-4 md:px-5 py-2 sm:py-3 bg-gray-50 border border-gray-200 rounded-lg sm:rounded-xl md:rounded-2xl outline-none focus:ring-2 transition-all text-sm sm:text-base" 
                    style={{ '--tw-ring-color': '#059669' }}
                    placeholder="Enter section heading..."
                    required
                    disabled={saving}
                  />
                </div>

                {/* Section Content Editor */}
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2 uppercase tracking-wider">Section Content</label>
                  <div className="bg-white rounded-lg sm:rounded-xl md:rounded-2xl border border-gray-200 overflow-hidden jodit-editor-container">
                    <JoditEditor
                      value={content}
                      config={{
                        ...config,
                        height: window.innerWidth < 640 ? 250 : 400
                      }}
                      onBlur={newContent => setContent(newContent)}
                      onChange={newContent => {}}
                    />
                  </div>
                </div>

                {/* Submit Button - PolicyDetail*/}
                <button 
                  type="submit" 
                  style={{ backgroundColor: '#059669' }} 
                  className={`w-full py-3 sm:py-4 text-white rounded-xl sm:rounded-2xl font-bold shadow-xl hover:brightness-110 transition-all text-sm sm:text-base md:text-lg flex items-center justify-center ${saving ? 'opacity-70 cursor-not-allowed' : ''} mt-4 sm:mt-6`}
                  disabled={saving || !heading.trim()}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        @keyframes fadeIn { 
          from { 
            opacity: 0; 
            transform: translateY(10px); 
          } 
          to { 
            opacity: 1; 
            transform: translateY(0); 
          } 
        }
        
        @keyframes modalIn { 
          from { 
            opacity: 0; 
            transform: scale(0.95); 
          } 
          to { 
            opacity: 1; 
            transform: scale(1); 
          } 
        }
        
        .animate-fadeIn { 
          animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        .animate-modalIn { 
          animation: modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        /* Custom description styling - PolicyDetail के समान */
        .custom-description {
          color: #4b5563 !important;
          font-size: 1rem !important;
          line-height: 1.6 !important;
        }
        
        .custom-description p {
          margin-bottom: 1rem !important;
        }
        
        .custom-description h1 {
          font-size: 1.75rem !important;
          font-weight: 700 !important;
          margin-top: 1.5rem !important;
          margin-bottom: 1rem !important;
          color: #111827 !important;
        }
        
        .custom-description h2 {
          font-size: 1.5rem !important;
          font-weight: 600 !important;
          margin-top: 1.25rem !important;
          margin-bottom: 0.75rem !important;
          color: #1f2937 !important;
        }
        
        .custom-description h3 {
          font-size: 1.25rem !important;
          font-weight: 600 !important;
          margin-top: 1rem !important;
          margin-bottom: 0.5rem !important;
          color: #374151 !important;
        }
        
        .custom-description ul {
          list-style-type: disc !important;
          padding-left: 1.25rem !important;
          margin-bottom: 1rem !important;
        }
        
        .custom-description ol {
          list-style-type: decimal !important;
          padding-left: 1.25rem !important;
          margin-bottom: 1rem !important;
        }
        
        .custom-description li {
          margin-bottom: 0.375rem !important;
        }
        
        .custom-description strong {
          font-weight: 600 !important;
          color: #111827 !important;
        }
        
        .custom-description em {
          font-style: italic !important;
        }
        
        .custom-description a {
          color: #059669 !important;
          text-decoration: underline !important;
        }
        
        .custom-description a:hover {
          color: #047857 !important;
        }
        
        .custom-description blockquote {
          border-left: 3px solid #059669 !important;
          padding-left: 1rem !important;
          margin: 1rem 0 !important;
          font-style: italic !important;
          color: #6b7280 !important;
        }
        
        .custom-description table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin: 1rem 0 !important;
          font-size: 0.875rem !important;
        }
        
        .custom-description th {
          background-color: #f9fafb !important;
          font-weight: 600 !important;
          padding: 0.5rem !important;
          border: 1px solid #e5e7eb !important;
        }
        
        .custom-description td {
          padding: 0.5rem !important;
          border: 1px solid #e5e7eb !important;
        }
        
        .custom-description img {
          max-width: 100% !important;
          height: auto !important;
          border-radius: 0.375rem !important;
          margin: 1rem 0 !important;
        }
        
        /* Remove background and borders from rich text output */
        .custom-description * { 
          background-color: transparent !important; 
          border: none !important; 
        }
        
        /* Jodit Editor Custom Styling - PolicyDetail के समान */
        .jodit-editor-container :global(.jodit-container) {
          border: none !important;
          background: #f9fafb !important;
          min-height: 200px;
        }
        
        .jodit-editor-container :global(.jodit-workplace) {
          background: #f9fafb !important;
          min-height: 150px;
        }
        
        .jodit-editor-container :global(.jodit-toolbar__box) {
          background: #f9fafb !important;
          border-bottom: 1px solid #eee !important;
          padding: 4px !important;
        }
        
        .jodit-editor-container :global(.jodit-wysiwyg) {
          background: #f9fafb !important;
          padding: 12px !important;
          min-height: 150px;
          font-size: 14px !important;
        }
        
        /* Responsive design improvements - PolicyDetail के समान */
        @media (min-width: 640px) {
          .custom-description {
            font-size: 1.0625rem !important;
            line-height: 1.7 !important;
          }
          
          .custom-description h1 {
            font-size: 2rem !important;
          }
          
          .custom-description h2 {
            font-size: 1.75rem !important;
          }
          
          .custom-description h3 {
            font-size: 1.5rem !important;
          }
          
          .custom-description table {
            font-size: 0.9375rem !important;
          }
          
          .jodit-editor-container :global(.jodit-wysiwyg) {
            font-size: 15px !important;
          }
        }
        
        @media (min-width: 768px) {
          .custom-description {
            font-size: 1.125rem !important;
            line-height: 1.8 !important;
          }
          
          .custom-description h1 {
            font-size: 2.25rem !important;
          }
          
          .custom-description h2 {
            font-size: 2rem !important;
          }
          
          .custom-description h3 {
            font-size: 1.75rem !important;
          }
          
          .custom-description table {
            font-size: 1rem !important;
          }
        }
        
        @media (min-width: 1024px) {
          .custom-description h1 {
            font-size: 2.5rem !important;
          }
        }
        
        /* Mobile-specific improvements - PolicyDetail के समान */
        @media (max-width: 640px) {
          .jodit-editor-container :global(.jodit-toolbar) {
            overflow-x: auto !important;
            flex-wrap: nowrap !important;
          }
          
          .jodit-editor-container :global(.jodit-toolbar__box) {
            min-width: max-content !important;
          }
          
          .custom-description table {
            display: block !important;
            overflow-x: auto !important;
            white-space: nowrap !important;
          }
        }
        
        /* Touch device improvements - PolicyDetail के समान */
        @media (hover: none) and (pointer: coarse) {
          button, 
          input, 
          [role="button"] {
            min-height: 44px !important;
            min-width: 44px !important;
          }
          
          .jodit-editor-container :global(.jodit-toolbar-button) {
            min-height: 40px !important;
            min-width: 40px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ItemDetail;